import { defineStore } from "pinia";
import { Entity } from "@/editor/blueprints/Entity";
import { GameNodeIdentifier } from "@/client/renderer";
import { LayerKind } from "@/editor/blueprints/LayerKind";
import { use_editor_store } from "@/editor/stores/editor";
import { Tile } from "@/editor/blueprints/Tile";
import { reactive, toRefs } from "vue";

export interface InspectorStore {
  component_stores: {
    module: string;
    tile: string;
    map: string;
    game_node: {
      scene_resource_path?: string;
      selected_game_node_id?: string;
      selected_entity_id?: Entity | null;
      is_instance?: boolean;
    };
    character_animation: {
      character_animation_resource_path?: string;
      selected_state_id?: number;
    };
    placing_config: {
      node_to_place_in: GameNodeIdentifier | null;
      placement_type: "background" | "object" | "blueprint";
      layer: LayerKind;
      resource_path: string | null;
      tile_id: number | null;
      node_selection_in_progress: boolean;
    };
  };
}

export const use_inspector_store = defineStore(
  "inspector",
  () => {
    const state: InspectorStore = reactive({
      component_stores: {
        game_node: {},
        map: "",
        module: "",
        character_animation: {},
        tile: "",
        placing_config: {
          node_to_place_in: null,
          placement_type: "background",
          layer: "Terrain",
          resource_path: null,
          tile_id: null,
          node_selection_in_progress: false,
        },
      },
    });

    const actions = {
      select_character_animation_state(
        selected_state_id: number,
        character_animation_resource_path: string,
      ) {
        state.component_stores = {
          ...state.component_stores,
          character_animation: {
            selected_state_id,
            character_animation_resource_path,
          },
        };
      },
      set_resource_path(resource_path: string) {
        state.component_stores = {
          ...state.component_stores,
          placing_config: {
            ...state.component_stores.placing_config,
            resource_path,
          },
        };
      },
      set_tile_settings(tile_id: number, tile_resource_path: string) {
        state.component_stores = {
          ...state.component_stores,
          placing_config: {
            ...state.component_stores.placing_config,
            tile_id,
            resource_path: tile_resource_path,
          },
        };
      },
      set_placement_type(
        placement_type: InspectorStore["component_stores"]["placing_config"]["placement_type"],
      ) {
        state.component_stores = {
          ...state.component_stores,
          placing_config: {
            ...state.component_stores.placing_config,
            tile_id: null,
            resource_path: null,
            placement_type,
          },
        };
      },
      set_placement_render_layer(layer: LayerKind) {
        state.component_stores = {
          ...state.component_stores,
          placing_config: {
            ...state.component_stores.placing_config,
            layer,
          },
        };
      },
      start_node_selection() {
        state.component_stores = {
          ...state.component_stores,
          placing_config: {
            ...state.component_stores.placing_config,
            node_selection_in_progress: true,
          },
        };
      },
      select_node_to_place_in({ game_node_id, entity_id }: GameNodeIdentifier) {
        console.log("selecting node to place in", game_node_id, entity_id);
        state.component_stores = {
          ...state.component_stores,
          placing_config: {
            ...state.component_stores.placing_config,
            node_to_place_in: {
              game_node_id,
              entity_id,
            },
            node_selection_in_progress: false,
          },
        };
      },
      select_game_node(
        scene_resource_path: string,
        selected_game_node_id: string,
        is_instance: boolean,
        selected_entity_id: Entity | null,
      ) {
        if (state.component_stores.placing_config.node_selection_in_progress) {
          this.select_node_to_place_in({
            game_node_id: selected_game_node_id,
            entity_id: selected_entity_id,
          });
          return;
        }
        state.component_stores = {
          ...state.component_stores,
          game_node: {
            scene_resource_path,
            selected_game_node_id,
            is_instance,
            selected_entity_id,
          },
        };
        const { set_inspector_component } = use_editor_store();
        set_inspector_component("game_node");
      },
    };

    return { ...toRefs(state), ...actions };
  },
  {
    persist: {
      afterRestore: (ctx) => {
        console.log("restored", ctx);
      },
    },
  },
);
